import { Grid, Typography } from "@mui/material";
import { IAircraft, IFlight, IFlightError, IOCAPIError } from "@vmosolutions/oc-interfaces";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { FlightError } from "../FlightErrors/FlightError";
import {
  setSelectedEvents as setSelectInTimeline,
  setFocusedEvents as setFocusInTimeline,
} from "src/redux/actions/flights/FlightTimeline.Actions";
import { FlightSummary } from "src/navigation/AppStatusBar/StatusBarSummary";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import moment from "moment-timezone";
interface IFlightErrorProps {
  id: string;
}

export const flightErrorTableInfo: ITableColumn[] = [
  {
    id: "flightId",
    value: "flightId",
    index: 0,
    numeric: false,
    label: "Flight ID",
    type: "string",
    width: 10,
    visible: false,
    filter: false,
  },
  {
    id: "title",
    value: "title",
    index: 1,
    numeric: false,
    label: "Error",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "description",
    value: "description",
    index: 2,
    numeric: false,
    label: "Description",
    type: "string",
    width: 10,
    visible: false,
    filter: true,
  },
  {
    id: "aircraftReg",
    value: "aircraftReg",
    index: 3,
    numeric: false,
    label: "Aircraft",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "priority",
    value: "priority",
    index: 4,
    numeric: false,
    label: "Priority",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "actualOut",
    value: "actualOut",
    index: 5,
    numeric: true,
    label: "Time [UTC]",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
];

export const flightErrorsDesc = {
  tableTitle: "Flight Errors",
  tableDescription: "",
  noMessage: "No Errors found",
};

interface IOption {
  value: number | string;
  label: string;
}

// Options for how far forward (from current date) to filter flight errors (by actualOut)
export const errorForwardOptions: IOption[] = [
  { value: 24, label: "24 Hours" },
  { value: 72, label: "3 Days" },
  { value: 168, label: "7 Days" },
  { value: 720, label: "30 Days" },
  { value: 9999, label: "All" },
];

const priorityOptions: IOption[] = [
  { value: "ALL", label: "All" },
  { value: "CRITICAL", label: "Critical" },
  { value: "WARNING", label: "Warning" },
];

interface IAircraftMap {
  [key: string]: IAircraft;
}

export const FlightErrorTable = (props: IFlightErrorProps): JSX.Element => {
  const { id } = props;
  const flightErrors = useSelector((state: IRootState) => state.flightScanner.flightErrors);
  const aircraftList = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  const dispatch = useDispatch();

  const [forwardFilter, setforwardFilter] = useState<IOption>(errorForwardOptions[0]);
  const [priorityFilter, setPriorityFilter] = useState<IOption>(priorityOptions[0]);
  const [selectedFlight, setSelectedFlight] = useState<IFlight | null>(null);
  const [loadingFlightErrors, setLoadingFlightErrors] = useState<boolean>(false);

  /*
  const timeLineselectedEvents = useSelector((state: IRootState) => state.timelineFlights.selectedEvents);
  const isFlightSelectedInTimeline = useMemo(() => {
    if (timeLineselectedEvents?.length > 0) {
      const foundMatch = timeLineselectedEvents.find((event) => event?._id === selectedFlight?._id);
      return !!foundMatch;
    }
    return false;
  }, [timeLineselectedEvents, selectedFlight]);
  */

  const aircraftMap = useMemo(() => {
    const map: IAircraftMap = {};
    aircraftList.forEach((aircraft: IAircraft) => {
      map[aircraft._id] = aircraft;
    });
    return map;
  }, [aircraftList]);

  const data = useMemo(() => {
    if (!aircraftMap) {
      return null;
    }

    const now = new Date();
    const filterUntil: number | null =
      forwardFilter.label !== "All" ? moment(now).utc(true).add(forwardFilter.value, "hours")?.valueOf() : null;

    return flightErrors.reduce((filtered, error: IFlightError) => {
      const outTime = new Date(error.actualOut)?.valueOf();
      //console.log("outTime", new Date(outTime).toUTCString());
      //console.log("filterUntil", new Date(filterUntil).toUTCString());
      if (
        (!filterUntil || outTime <= filterUntil) &&
        (priorityFilter.value === "ALL" || priorityFilter.value === error.priority)
      ) {
        filtered.push({
          title: error.title,
          aircraftReg: error.aircraftId ? aircraftMap[error.aircraftId]?.registration : "Unassigned",
          description: error.description,
          priority: error.priority,
          flightId: error.flightId,
          actualOut: error.actualOut,
        });
      }
      return filtered;
    }, []);
  }, [aircraftMap, flightErrors, forwardFilter, priorityFilter]);

  const changeSelectedRows = useCallback(
    async (rows: any, focusSelected?: boolean, dataType?: string) => {
      const flightId = rows[0]?.flightId;
      if (flightId) {
        setLoadingFlightErrors(true);
        setSelectedFlight(null);
        const flight: IFlight | IOCAPIError | null = await AmplifyAPIWrapper.Get<IFlight>(`/flights/${flightId}`);
        if (flight) {
          setSelectedFlight(flight);
          dispatch(setSelectInTimeline([flight]));
          dispatch(
            setFocusInTimeline({
              events: [flight],
              shouldFocus: focusSelected ? "all" : "none",
            })
          );
          setLoadingFlightErrors(false);
          return;
        }
      }
      setLoadingFlightErrors(false);
      setSelectedFlight(null);
    },
    [dispatch]
  );

  // const handleClick = useCallback(() => {
  //   dispatch(openSelectedEventWindow(selectedFlight, []));
  // }, [dispatch, selectedFlight]);

  const tableData = useMemo(() => {
    return [
      {
        id: "flightErrors",
        dataType: "FlightErrors",
        label: "Flight Errors",
        data,
        tableInfo: flightErrorTableInfo,
        tableInfoDisplay: flightErrorsDesc,
      },
    ];
  }, [data]);

  // if (data?.length === 0) {
  //   return (
  //     <Grid item className="no_errors_block">
  //       No Flight Errors Found
  //     </Grid>
  //   );
  // }

  return (
    <Grid container direction="column">
      <Grid item style={{ flexGrow: 1 }}>
        <FilterSearchTable
          className="error_table"
          id={`flight_errors_table_${id}`}
          parentId={`${id}-panel`}
          events={tableData}
          uniqueKey={"id"}
          changeSelectedRows={changeSelectedRows}
          selectModeSingle={true}
          defaultRowsPerPage={10}
          optionalControls={
            <Grid container>
              <Grid item>
                <SelectField
                  className="flight_error_type_selector"
                  name="flight_error_type_selector"
                  label="Filter Priority"
                  isMulti={false}
                  value={priorityFilter}
                  onChange={setPriorityFilter}
                  options={priorityOptions}
                />
              </Grid>
              <Grid container alignItems="center" style={{ width: "auto" }}>
                <Grid item>
                  <SelectField
                    className="flight_error_forward_selector"
                    name="flight_error_forward_selector"
                    label="Filter Forward"
                    isMulti={false}
                    value={forwardFilter}
                    onChange={setforwardFilter}
                    options={errorForwardOptions}
                  />
                </Grid>
                <Typography variant="caption">{`(Only show errors on past flights and ${forwardFilter.label} ahead.)`}</Typography>
              </Grid>
              {selectedFlight && (
                <Grid container style={{ padding: "10px 0px" }}>
                  <Grid item style={{ margin: 10 }}>
                    <FlightSummary flight={selectedFlight} crew={null} eventSelected={false} />
                  </Grid>
                  <FlightError flightError={{ flight: selectedFlight }} />
                </Grid>
              )}
              {!selectedFlight && loadingFlightErrors && (
                <Grid item className="spinner_container">
                  <SpinnerProgress
                    loading={loadingFlightErrors}
                    message="loading all errors associated with selected flight"
                  />
                </Grid>
              )}
            </Grid>
          }
        />
      </Grid>
    </Grid>
  );
};
